import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

// FDI-Schema, Behandlersicht (Patient sitzt gegenueber, daher gespiegelt):
//
//   Oberkiefer:  18 17 16 15 14 13 12 11 | 21 22 23 24 25 26 27 28
//   Unterkiefer: 48 47 46 45 44 43 42 41 | 31 32 33 34 35 36 37 38
//
// Milchgebiss (deciduous):
//   55 54 53 52 51 | 61 62 63 64 65
//   85 84 83 82 81 | 71 72 73 74 75
//
// Patientensicht (UR=upper right, UL=upper left, ...) waere genau umgekehrt,
// aber die Behandlersicht ist in DACH-Praxen die ubliche Darstellung.

const UPPER_LEFT  = ['18', '17', '16', '15', '14', '13', '12', '11'];
const UPPER_RIGHT = ['21', '22', '23', '24', '25', '26', '27', '28'];
const LOWER_LEFT  = ['48', '47', '46', '45', '44', '43', '42', '41'];
const LOWER_RIGHT = ['31', '32', '33', '34', '35', '36', '37', '38'];

const D_UPPER_LEFT  = ['55', '54', '53', '52', '51'];
const D_UPPER_RIGHT = ['61', '62', '63', '64', '65'];
const D_LOWER_LEFT  = ['85', '84', '83', '82', '81'];
const D_LOWER_RIGHT = ['71', '72', '73', '74', '75'];

export interface ToothChartProps {
    value: string[];
    onChange?: (next: string[]) => void;
    readOnly?: boolean;
    /**
     * Wenn true, kann der User per Toggle zwischen permanentem und Milchgebiss
     * wechseln. Werte aus beiden Modi koennen koexistieren im value-Array.
     */
    allowDeciduous?: boolean;
}

const ToothChart: React.FC<ToothChartProps> = ({ value, onChange, readOnly = false, allowDeciduous = false }) => {
    const { t } = useTranslation();
    const [showDeciduous, setShowDeciduous] = useState(false);
    const selected = new Set(value);

    const toggle = (code: string) => {
        if (readOnly || !onChange) return;
        const next = new Set(selected);
        if (next.has(code)) next.delete(code); else next.add(code);
        onChange(Array.from(next).sort());
    };

    const renderTooth = (code: string) => {
        const isSelected = selected.has(code);
        const base = 'h-9 w-full rounded-md text-xs font-medium tabular-nums transition-colors border';
        const styled = isSelected
            ? 'bg-brand-primary text-white border-brand-primary'
            : 'bg-brand-surface text-brand-text-muted border-brand-border hover:bg-brand-surface-2 hover:text-brand-text';
        const cursor = readOnly ? 'cursor-default' : 'cursor-pointer';
        return (
            <button
                key={code}
                type="button"
                disabled={readOnly}
                onClick={() => toggle(code)}
                className={`${base} ${styled} ${cursor}`}
                aria-pressed={isSelected}
                aria-label={t('notes.tooth', { code })}
                title={t('notes.tooth', { code })}
            >
                {code}
            </button>
        );
    };

    const Row: React.FC<{ left: string[]; right: string[]; label: string }> = ({ left, right, label }) => {
        const cols = left.length === 8 ? 'grid-cols-8' : 'grid-cols-5';
        return (
            <div className="flex items-center gap-2">
                <div className="w-14 shrink-0 text-[10px] font-medium uppercase tracking-wider text-brand-text-muted">{label}</div>
                <div className={`grid ${cols} gap-1 flex-1`}>
                    {left.map(renderTooth)}
                </div>
                <div className="w-px self-stretch bg-brand-border mx-1" aria-hidden />
                <div className={`grid ${cols} gap-1 flex-1`}>
                    {right.map(renderTooth)}
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-1.5 select-none">
            {allowDeciduous && !readOnly && (
                <div className="flex justify-end -mt-2 mb-2">
                    <div className="inline-flex border border-brand-border rounded-md overflow-hidden text-[11px] font-medium">
                        <button
                            type="button"
                            onClick={() => setShowDeciduous(false)}
                            className={
                                !showDeciduous
                                    ? 'px-2.5 py-1 bg-brand-primary text-white cursor-pointer transition-colors'
                                    : 'px-2.5 py-1 bg-brand-surface text-brand-text-muted hover:bg-brand-surface-2 cursor-pointer transition-colors'
                            }
                            aria-pressed={!showDeciduous}
                        >
                            {t('notes.permanent')}
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowDeciduous(true)}
                            className={
                                showDeciduous
                                    ? 'px-2.5 py-1 bg-brand-primary text-white cursor-pointer transition-colors'
                                    : 'px-2.5 py-1 bg-brand-surface text-brand-text-muted hover:bg-brand-surface-2 cursor-pointer transition-colors'
                            }
                            aria-pressed={showDeciduous}
                        >
                            {t('notes.deciduous')}
                        </button>
                    </div>
                </div>
            )}
            {showDeciduous ? (
                <>
                    <Row left={D_UPPER_LEFT} right={D_UPPER_RIGHT} label={t('notes.rowUpper')} />
                    <Row left={D_LOWER_LEFT} right={D_LOWER_RIGHT} label={t('notes.rowLower')} />
                </>
            ) : (
                <>
                    <Row left={UPPER_LEFT} right={UPPER_RIGHT} label={t('notes.rowUpper')} />
                    <Row left={LOWER_LEFT} right={LOWER_RIGHT} label={t('notes.rowLower')} />
                </>
            )}
        </div>
    );
};

export default ToothChart;
